import { GetRegistrationModel } from "@factory/model/query-model-registration";
import { calendarUtils } from "@shared/utility/calendar-utils/calendar-utils";
import { useMemo } from "react";

export function useCalendarEvents(registrationData: GetRegistrationModel[]) {
  /**
   * Checks if a registration has a valid date that can be displayed on the calendar.
   * Time is optional - events without time will be shown as all-day events.
   */
  const hasValidDate = (registration: GetRegistrationModel): boolean => {
    if (!registration.date) {
      return false;
    }
    // Verify that the date can be parsed into a valid date
    const dateStr = calendarUtils.combineDateTime(
      registration.date,
      registration.time,
    );
    return dateStr !== "";
  };

  const preAssignedEvents = useMemo(
    () => registrationData.filter(hasValidDate),
    [registrationData],
  );

  const unassignedEvents = useMemo(
    () =>
      registrationData.filter((registration) => !hasValidDate(registration)),
    [registrationData],
  );

  const calendarEvents = useMemo(
    () =>
      preAssignedEvents
        .map((event) => {
          const start = calendarUtils.combineDateTime(event.date!, event.time);
          const end = calendarUtils.addHourToTime(event.date!, event.time);

          // Skip events with invalid start date
          if (!start) {
            return null;
          }

          // If no end time (all-day event), set allDay flag
          const isAllDay = !event.time || !end;

          return {
            title: calendarUtils.formatClientName(event),
            start,
            end: isAllDay ? undefined : end,
            allDay: isAllDay,
            extendedProps: event,
          };
        })
        .filter((event): event is NonNullable<typeof event> => event !== null),
    [preAssignedEvents],
  );

  return {
    preAssignedEvents,
    unassignedEvents,
    calendarEvents,
    hasValidDate,
  };
}
